'use client';

import { useMemo, useState } from 'react';
import Image from 'next/image';
import { Mail } from 'lucide-react';
import { useAuth } from '@/context/AuthContext/AuthContext';
import { GroupRole } from '@/types';
import { useLanguage } from '@/context/LanguageContext';
import { getStrapiImageUrl } from '@/lib/image-utils';
import { BulkEmailModal, Modal, Toast } from '@/components/ui';
import { useBulkEmail } from '@/hooks/useBulkEmail';

const baseUrl = '/api/strapi';

interface GroupMemberDetail {
  id: number;
  documentId: string;
  role: GroupRole;
  approvalStatus: 'pending' | 'approved' | 'canceled';
  createdAt: string;
  user: {
    id: number;
    username: string;
    displayName?: string;
    initials?: string;
    avatarColor?: string;
    avatarUrl?: string;
    ageGroup?: 'age_18_plus' | 'under_18';
  };
}

interface MembersTabProps {
  members: GroupMemberDetail[];
  groupId: number;
  isMember: boolean;
  isAdmin: boolean;
  canSendBulkEmail?: boolean;
  onUpdate?: () => void;
}

export function MembersTab({ members, groupId, isAdmin, canSendBulkEmail = false, onUpdate }: MembersTabProps) {
  const { t, language } = useLanguage();
  const { users } = useAuth();
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [memberToReject, setMemberToReject] = useState<GroupMemberDetail | null>(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [sendingRejection, setSendingRejection] = useState(false);
  const [pendingMemberActionValues, setPendingMemberActionValues] = useState<Record<number, string>>({});

  const approvedMembers = useMemo(
    () => members.filter((m) => m.approvalStatus === 'approved'),
    [members]
  );
  const pendingMembers = useMemo(
    () => members.filter((m) => m.approvalStatus === 'pending'),
    [members]
  );
  const approvedAdminCount = useMemo(
    () => approvedMembers.filter((m) => m.role === 'admin').length,
    [approvedMembers]
  );

  const getUserData = (userId: number) => users.find((u) => u.id === userId);

  const showTimedToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const bulkEmail = useBulkEmail({
    groupId,
    approvedMembers,
    messages: {
      validationError: t.groupTabs.bulkEmailValidationError,
      sent: t.groupTabs.bulkEmailSent,
      sendFailed: t.groupTabs.bulkEmailSendFailed,
      forbidden: t.groupTabs.bulkEmailForbidden,
    },
    onToast: showTimedToast,
  });

  const getMemberDisplayName = (member: GroupMemberDetail) => {
    return getUserData(member.user.id)?.displayName || member.user.displayName || member.user.username;
  };

  const getTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return t.groupTabs.today;
    if (days === 1) return t.groupTabs.oneDayAgo;
    if (days < 7) return `${days} ${t.groupTabs.daysAgo}`;
    if (days < 14) return t.groupTabs.oneWeekAgo;
    return `${Math.floor(days / 7)} ${t.groupTabs.weeksAgo}`;
  };

  const handleAccept = async (member: GroupMemberDetail) => {
    try {
      const res = await fetch(`${baseUrl}/group-members/${member.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { approvalStatus: 'approved' } }),
      });
      if (!res.ok) throw new Error('Failed to approve member');
      onUpdate?.();
    } catch (error) {
      console.error('Failed to approve member:', error);
      alert('Failed to approve member');
    }
  };

  const closeRejectModal = () => {
    if (sendingRejection) return;
    setShowRejectModal(false);
    setMemberToReject(null);
    setRejectionNote('');
  };

  const resetPendingMemberAction = (memberId: number) => {
    setPendingMemberActionValues((prev) => ({ ...prev, [memberId]: '' }));
  };

  const handleReject = (member: GroupMemberDetail) => {
    resetPendingMemberAction(member.id);
    setMemberToReject(member);
    setRejectionNote('');
    setShowRejectModal(true);
  };

  const handleConfirmReject = async () => {
    if (!memberToReject) return;

    const note = rejectionNote.trim();

    if (!note) {
      showTimedToast(t.groupTabs.rejectionReasonRequired, 'error');
      return;
    }

    setSendingRejection(true);

    const rejectedMember = memberToReject;
    let membershipUpdated = false;
    let rejectionMessageSent = false;

    try {
      const res = await fetch(`${baseUrl}/group-members/${rejectedMember.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { approvalStatus: 'canceled' } }),
      });

      if (!res.ok) throw new Error('Failed to reject member');

      membershipUpdated = true;

      const emailRes = await fetch(`${baseUrl}/email/send-rejection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          groupId,
          groupMemberDocumentId: rejectedMember.documentId,
          note,
        }),
      });

      if (!emailRes.ok) throw new Error('Failed to send rejection message');

      rejectionMessageSent = true;

      showTimedToast(
        t.groupTabs.rejectSuccess.replace('{name}', rejectedMember.user.displayName || rejectedMember.user.username),
        'success',
      );
    } catch (error) {
      console.error('src/components/group-tabs/MembersTab.tsx', error);
      showTimedToast(
        membershipUpdated
          ? t.groupTabs.rejectMessageFailed.replace('{name}', rejectedMember.user.displayName || rejectedMember.user.username)
          : t.groupTabs.rejectFailed,
        'error',
      );
    } finally {
      if (membershipUpdated) {
        try {
          onUpdate?.();
        } catch (error) {
          console.error('src/components/group-tabs/MembersTab.tsx', error);
          // Best-effort refresh.
        }

        if (rejectionMessageSent) {
          setShowRejectModal(false);
          setMemberToReject(null);
          setRejectionNote('');
        }
      }

      setSendingRejection(false);
    }
  };

  const handleRoleChange = async (member: GroupMemberDetail, newRole: GroupRole) => {
    if (member.role === newRole) return;

    if (member.role === 'admin' && newRole !== 'admin' && approvedAdminCount <= 1) {
      showTimedToast(t.groupTabs.lastAdminError, 'error');
      return;
    }

    try {
      const res = await fetch(`${baseUrl}/group-members/${member.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { role: newRole } }),
      });
      if (!res.ok) throw new Error('Failed to update role');
      showTimedToast(
        t.groupTabs.roleUpdateSuccess
          .replace('{name}', getMemberDisplayName(member))
          .replace('{role}', newRole),
        'success',
      );
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update role:', error);
      showTimedToast(t.groupTabs.roleUpdateFailed, 'error');
    }
  };

  const handleBulkRemove = async () => {
    const membersToRemove = approvedMembers.filter((m) => bulkEmail.selectedMemberIds.includes(m.user.id));
    if (membersToRemove.length === 0) return;

    const adminBeingRemoved = membersToRemove.filter((m) => m.role === 'admin').length;
    if (adminBeingRemoved >= approvedAdminCount) {
      alert('At least one admin is required. Assign another admin first.');
      return;
    }

    const confirmed = window.confirm(
      t.groupTabs.bulkRemoveConfirm.replace('{count}', String(membersToRemove.length)),
    );
    if (!confirmed) return;

    try {
      await Promise.all(
        membersToRemove.map((member) =>
          fetch(`${baseUrl}/group-members/${member.documentId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data: { approvalStatus: 'canceled' } }),
          }),
        ),
      );
      onUpdate?.();
    } catch (error) {
      console.error('Failed to remove members:', error);
      showTimedToast(t.groupTabs.bulkRemoveFailed, 'error');
    }
  };

  const renderMemberIdentity = (member: GroupMemberDetail) => {
    const userData = getUserData(member.user.id);
    const avatarUrl = userData?.avatarUrl || (member.user.avatarUrl ? getStrapiImageUrl(member.user.avatarUrl) : null);
    const displayName = getMemberDisplayName(member);
    const initials = userData?.initials || member.user.initials || member.user.username.substring(0, 2).toUpperCase();
    const avatarColor = userData?.avatarColor || member.user.avatarColor || '#00423E';

    return (
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-9 h-9 rounded-full overflow-hidden shrink-0 flex items-center justify-center text-xs font-medium text-white"
          style={{ backgroundColor: avatarUrl ? undefined : avatarColor }}
        >
          {avatarUrl ? (
            <Image src={avatarUrl} alt={displayName} width={36} height={36} className="w-full h-full object-contain" unoptimized />
          ) : (
            initials
          )}
        </div>
        <p className="font-medium text-foreground truncate">{displayName}</p>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground" data-testid="group-members-tab-heading">{t.groupTabs.members} ({approvedMembers.length})</h2>
      </div>

      {isAdmin && (
        <div className="space-y-3">
          <h3 className="text-lg font-medium text-foreground" data-testid="group-members-tab-pending-heading">{t.groupTabs.memberRequests} ({pendingMembers.length})</h3>
          {pendingMembers.length === 0 ? (
            <div data-testid="group-members-tab-pending-empty-state" className="text-sm text-foreground-muted">No pending requests</div>
          ) : (
            <div className="bg-white border border-border rounded-lg overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead className="bg-primary border-b border-border">
                  <tr>
                    <th className="text-left text-xs font-semibold text-white px-4 py-3">Name</th>
                    <th className="text-left text-xs font-semibold text-white px-4 py-3">Role</th>
                    <th className="text-left text-xs font-semibold text-white px-4 py-3">Request Date</th>
                    <th className="text-right text-xs font-semibold text-white px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingMembers.map((member) => (
                    <tr key={member.id} data-testid={`group-members-tab-pending-row-${member.id}`} className="border-t border-border">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          {renderMemberIdentity(member)}
                          {member.user.ageGroup === 'age_18_plus' ? (
                            <span className="shrink-0 text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700">{t.groupTabs.ageTagAdult}</span>
                          ) : member.user.ageGroup === 'under_18' ? (
                            <span className="shrink-0 text-xs font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{t.groupTabs.ageTagTeen}</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground-muted capitalize">{member.role}</td>
                      <td className="px-4 py-3 text-sm text-foreground-muted">{getTimeAgo(member.createdAt)}</td>
                      <td className="px-4 py-3 text-right">
                        <select
                          value={pendingMemberActionValues[member.id] ?? ''}
                          onChange={(e) => {
                            const action = e.target.value;
                            resetPendingMemberAction(member.id);
                            if (action === 'accept') void handleAccept(member);
                            if (action === 'reject') handleReject(member);
                          }}
                          data-testid="group-members-tab-member-action-select"
                          className="text-sm border border-border rounded px-2 py-1 bg-background text-foreground"
                        >
                          <option value="" disabled>Select</option>
                          <option value="accept">Accept</option>
                          <option value="reject">Reject</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        <h3 className="text-lg font-medium text-foreground" data-testid="group-members-tab-current-members-heading">{t.groupTabs.currentMembers}</h3>
        {(canSendBulkEmail || isAdmin) && bulkEmail.selectedMemberIds.length > 0 && (
          <div className="flex items-center gap-3 p-3 bg-background-secondary rounded-lg flex-wrap">
            <span className="text-sm text-foreground-muted">
              {bulkEmail.selectedMemberIds.length} {t.groupTabs.selectedItems}
            </span>
            {canSendBulkEmail && (
              <button
                type="button"
                onClick={() => bulkEmail.setShowBulkEmailModal(true)}
                data-testid="group-members-tab-send-bulk-email-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-background rounded-md hover:opacity-90"
              >
                <Mail size={14} />
                {t.groupTabs.sendEmail}
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={() => void handleBulkRemove()}
                data-testid="group-members-tab-bulk-remove-btn"
                className="px-3 py-1.5 text-sm bg-primary text-background rounded-md hover:opacity-90"
              >
                {t.groupTabs.bulkRemove}
              </button>
            )}
          </div>
        )}
        {approvedMembers.length === 0 ? (
          <div data-testid="group-members-tab-no-members-state" className="text-center py-12 text-foreground-muted">{t.groupTabs.noMembersYet}</div>
        ) : (
          <div className="bg-white border border-border rounded-lg overflow-x-auto">
            <table className="w-full min-w-0">
              <thead className="bg-primary border-b border-border">
                <tr>
                  {(canSendBulkEmail || isAdmin) && (
                    <th className="w-10 px-3 py-3 sm:px-4">
                      <input
                        type="checkbox"
                        checked={approvedMembers.length > 0 && bulkEmail.selectedMemberIds.length === approvedMembers.length}
                        onChange={bulkEmail.toggleSelectAllMembers}
                        aria-label={t.groupTabs.selectAllMembers}
                        data-testid="group-members-tab-select-all-checkbox"
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      />
                    </th>
                  )}
                  <th className="text-left text-xs font-semibold text-white px-3 py-3 sm:px-4">Name</th>
                  <th className="text-left text-xs font-semibold text-white px-3 py-3 sm:px-4">Role</th>
                  <th className="hidden sm:table-cell text-left text-xs font-semibold text-white px-4 py-3">Member Since</th>
                </tr>
              </thead>
              <tbody>
                {approvedMembers.map((member) => {
                  const memberSince = new Date(member.createdAt).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                    month: 'short',
                    year: 'numeric',
                  });
                  return (
                    <tr key={member.id} data-testid={`group-members-tab-member-row-${member.id}`} className="border-t border-border">
                      {(canSendBulkEmail || isAdmin) && (
                        <td className="px-3 py-3 sm:px-4 align-middle">
                          <input
                            type="checkbox"
                            checked={bulkEmail.selectedMemberIds.includes(member.user.id)}
                            onChange={() => bulkEmail.toggleMemberSelection(member.user.id)}
                            aria-label={t.groupTabs.selectMember.replace('{name}', getMemberDisplayName(member))}
                            data-testid="group-members-tab-member-bulk-checkbox"
                            className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                          />
                        </td>
                      )}
                      <td className="px-3 py-3 sm:px-4">
                        <div className="min-w-0">
                          {renderMemberIdentity(member)}
                          <p className="sm:hidden text-xs text-foreground-muted mt-1 pl-12">{memberSince}</p>
                        </div>
                      </td>
                      <td className="px-3 py-3 sm:px-4">
                        {isAdmin ? (
                          <select
                            value={member.role}
                            onChange={(e) => handleRoleChange(member, e.target.value as GroupRole)}
                            data-testid="group-members-tab-member-role-select"
                            className="text-sm border border-border rounded px-2 py-1 bg-background text-foreground"
                          >
                            <option value="admin">Admin</option>
                            <option value="editor">Editor</option>
                            <option value="member">Member</option>
                          </select>
                        ) : (
                          <span className="text-sm text-foreground-muted capitalize">{member.role}</span>
                        )}
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 text-sm text-foreground-muted">
                        {memberSince}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BulkEmailModal
        isOpen={bulkEmail.showBulkEmailModal}
        onClose={bulkEmail.closeBulkEmailModal}
        selectedCount={bulkEmail.selectedMemberIds.length}
        subject={bulkEmail.bulkEmailSubject}
        onSubjectChange={bulkEmail.setBulkEmailSubject}
        message={bulkEmail.bulkEmailMessage}
        onMessageChange={bulkEmail.setBulkEmailMessage}
        sending={bulkEmail.sendingBulkEmail}
        onSend={bulkEmail.handleSendBulkEmail}
      />

      <div data-testid="group-members-tab-reject-modal-root">
        <Modal
          isOpen={showRejectModal}
          onClose={closeRejectModal}
          title={t.groupTabs.rejectModalTitle}
          subtitle={t.groupTabs.rejectModalSubtitle.replace('{name}', memberToReject?.user.displayName || memberToReject?.user.username || '')}
          footer={
            <>
              <button
                type="button"
                onClick={closeRejectModal}
                disabled={sendingRejection}
                data-testid="group-members-tab-reject-modal-cancel-btn"
                className="px-4 py-2 text-sm border border-border rounded-md text-foreground hover:bg-background-secondary disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t.groupTabs.cancel}
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmReject()}
                disabled={sendingRejection}
                data-testid="group-members-tab-reject-modal-submit-btn"
                className="px-4 py-2 text-sm bg-primary text-background rounded-md hover:opacity-90 disabled:cursor-not-allowed"
              >
                {sendingRejection ? t.groupTabs.sendingRejection : t.groupTabs.rejectAndSend}
              </button>
            </>
          }
        >
          <div>
            <label htmlFor="group-rejection-note" className="block text-sm font-medium text-foreground mb-1.5">
              {t.groupTabs.rejectionReasonLabel} *
            </label>
            <textarea
              id="group-rejection-note"
              value={rejectionNote}
              onChange={(event) => setRejectionNote(event.target.value)}
              placeholder={t.groupTabs.rejectionReasonPlaceholder}
              rows={5}
              data-testid="group-members-tab-rejection-note-textarea"
              className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>
        </Modal>
      </div>
    </div>
  );
}
